import { Link } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { Wordmark } from './Wordmark';
import { cn } from '../lib/classnames';
import { useAuthStore } from '../lib/auth';

interface LayoutProps {
  children: ReactNode;
}

const NAV: Array<{ to: string; label: string; mobile?: boolean }> = [
  { to: '/play', label: 'Play', mobile: true },
  { to: '/cards', label: 'Cards', mobile: true },
  { to: '/replays', label: 'Replays' },
  { to: '/help', label: 'Guide' },
];

export function Layout({ children }: LayoutProps) {
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const fetchMe = useAuthStore((s) => s.fetchMe);

  useEffect(() => {
    if (!initialized) void fetchMe();
  }, [fetchMe, initialized]);

  return (
    <div className="h-dvh w-dvw flex flex-col bg-bg bg-paper text-ink">
      <header className="shrink-0 h-14 border-b border-ink/8 bg-surface/80 backdrop-blur">
        <div className="h-full max-w-[1400px] mx-auto px-3 sm:px-5 flex items-center justify-between gap-2">
          <Link to="/" className="focus-ring rounded-md -ml-1 px-1 py-1 shrink-0" aria-label="Chess: Challenge Cards">
            <span className="sm:hidden font-display font-black wordmark-display select-none whitespace-nowrap text-xl leading-none">
              <span className="text-ink">Chess</span>
              <span className="text-accent mx-[0.08em]">:</span>
              <span className="text-ink">Cards</span>
            </span>
            <Wordmark size="sm" className="hidden sm:inline" />
          </Link>
          <nav className="flex shrink-0 items-center gap-0.5 sm:gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'px-2 sm:px-3 py-2 rounded-md text-sm font-medium text-ink-muted hover:text-ink hover:bg-ink/5 transition focus-ring',
                  !item.mobile && 'hidden md:inline-flex',
                )}
                activeProps={{ className: 'text-ink bg-ink/5' } as any}
              >
                {item.label}
              </Link>
            ))}
            <span className="hidden sm:block mx-2 h-5 w-px bg-ink/15" aria-hidden />
            <Link
              to="/support"
              className="h-9 w-9 sm:w-auto sm:px-3 grid sm:inline-flex place-items-center sm:items-center rounded-md text-sm font-semibold text-accent hover:bg-accent/10 transition focus-ring"
              aria-label="Support the Site"
            >
              <SupportIcon />
              <span className="hidden sm:inline">Support the Site</span>
            </Link>
            {user ? (
              <Link
                to="/profile/$username"
                params={{ username: user.username }}
                className="hidden sm:inline-flex px-3 py-2 rounded-md text-sm font-medium text-ink-muted hover:text-ink hover:bg-ink/5 transition focus-ring"
              >
                {user.displayName}
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden sm:inline-flex px-3 py-2 rounded-md text-sm font-medium text-ink-muted hover:text-ink hover:bg-ink/5 transition focus-ring"
                >
                  Log in
                </Link>
                <Link
                  to="/join"
                  className="hidden sm:inline-flex px-3 py-2 rounded-md text-sm font-semibold text-ink bg-ink/5 hover:bg-ink/10 transition focus-ring"
                >
                  Join
                </Link>
              </>
            )}
            <Link
              to="/settings"
              className="ml-1 h-9 w-9 grid place-items-center rounded-md text-ink-muted hover:text-ink hover:bg-ink/5 transition focus-ring"
              aria-label="Settings"
            >
              <SettingsIcon />
            </Link>
          </nav>
        </div>
      </header>
      <main data-main-scroll className={cn('flex-1 min-h-0 overflow-y-auto')}>{children}</main>
    </div>
  );
}

/**
 * Mount this at the top of a gameplay route to lock vertical scroll on the
 * main scroll container, so the chess board stays fully visible without the
 * player having to scroll mid-game.
 */
export function NoScroll() {
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.dataset['noScroll'];
    root.dataset['noScroll'] = 'true';
    return () => {
      if (prev === undefined) delete root.dataset['noScroll'];
      else root.dataset['noScroll'] = prev;
    };
  }, []);
  return null;
}

function SupportIcon() {
  return (
    <svg className="sm:hidden" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 21s-7-4.6-9.2-8.7C1.2 9.3 2.4 6 5.6 5.2c1.9-.5 3.8.3 4.9 1.8 1.1-1.5 3-2.3 4.9-1.8 3.2.8 4.4 4.1 2.8 7.1C19 16.4 12 21 12 21z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
